<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;

    // Components
    import { Icon } from '@oat-sa-private/ui-elements';
    // Utils
    import  { createEventDispatcher } from 'svelte';
    import { getActualKey } from '@oat-sa-private/ui-core';

    const dispatch = createEventDispatcher();

    let root = null;

    export let label = '';
    export let key;
    export let icon;
    export let togglable = false;
    export let checked = false;
    export let disabled = false;
    export let actionFocusTarget = null;
    // allow some columns to be hidden if they are empty:
    export let hasToggleColumn = true;
    export let hasIconColumn = true;
    export let active = false;
    export let preventScrollOnFocus = true;

    $: handleActiveStateChange(active);

    /**
     * Handle active state change
     * @param {boolean} state - specifies if item is activated or not
     */
    function handleActiveStateChange(state) {
        if (state && root) {
            root.focus({ preventScroll: preventScrollOnFocus });
        }
    }

    /**
     * Handle the activation of this item by mouse/touch
     * @fires action
     */
    function handleClick() {
        if (!disabled) {
            dispatch('action', { key, actionFocusTarget });
        }
    }
    /**
     * Handles the activation of this item by key press
     * @param {KeyboardEvent} event
     * @fires action
     */
    function handleKeydown(event) {
        const pressedKey = getActualKey(event);
        if (!disabled && (pressedKey === 'enter' || pressedKey === 'space')) {
            dispatch('action', { key, actionFocusTarget });
        }
    }
</script>

<style>
    .flyout-menu-item-button {
        display: flex;
        flex-wrap: nowrap;
        width: 100%;
        min-height: 5.5rem;
        padding: 1.25rem 1.5rem;
        border: none;
        background: none;
        color: var(--color-text-default);
        font-size: var(--fontsize-body-s);
        line-height: 1.5;
        text-align: inherit;
        cursor: pointer;

        &[aria-disabled='true'] {
            color: var(--color-text-disabled);
            cursor: default;

            & .toggle-column.checkbox {
                border-color: var(--color-border-disabled);
            }
        }

        &:hover {
            background: var(--color-bg-actionable-secondary-hover);
        }

        & .toggle-column {
            min-width: 2rem;
            margin-block: 0.25rem 0;
            margin-inline: 0 2rem;

            & :global(svg) {
                display: none;
            }
            &.checkbox {
                /* TODO: extract to mixin or element */
                border: solid var(--border-medium) var(--color-border-default);
                border-radius: var(--radius-medium);
                background-color: var(--color-bg-default);
                width: 2rem;
                height: 2rem;
                position: relative;

                /* checkbox check */
                & :global(svg) {
                    position: absolute;
                    left: -0.25rem;
                    width: 1.5rem;
                    height: 1.5rem;
                }
                &.checked :global(svg) {
                    display: inline;
                }
            }
        }
        & .icon-column {
            min-width: 2rem;
            margin-block: -0.125rem 0;
            margin-inline: 0 1.5rem;
        }
    }

    /* Focus styles - :global because of mixin */
    li :global(.flyout-menu-item-button) {
        @add-mixin outline-focus 0;
        &:focus-visible {
            background: var(--color-bg-actionable-secondary-hover);
        }
    }
</style>

<li role={togglable ? 'menuitemcheckbox' : 'menuitem'}>
    <button
        class="flyout-menu-item-button"
        role={togglable ? 'switch' : null}
        aria-checked={togglable ? checked : null}
        aria-disabled={disabled}
        {disabled}
        bind:this={root}
        on:keydown|preventDefault={handleKeydown}
        on:click={handleClick}
        tabindex="-1"
    >
        {#if hasToggleColumn}
            <span class="toggle-column" class:checkbox={togglable} class:checked={togglable && checked}>
                <Icon name="checkbox-check-16" ariaHidden={true} />
            </span>
        {/if}
        {#if hasIconColumn}
            <span class="icon-column">
                {#if icon}
                    <Icon name={icon} ariaHidden={true} />
                {/if}
            </span>
        {/if}
        <span class="text-column">{label}</span>
    </button>
</li>
